import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { ApiError } from '../api/client';

type Severity = 'success' | 'error' | 'info' | 'warning';
interface Toast {
  id: number;
  severity: Severity;
  message: string;
}

interface NotifyApi {
  notify: (severity: Severity, message: string) => void;
  notifyError: (err: unknown, context: string) => void;
}

const NotifyContext = createContext<NotifyApi | null>(null);

export function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.detail}${err.requestId ? ` (request ${err.requestId})` : ''}`;
  if (err instanceof Error) return err.message;
  return String(err);
}

export function NotifyProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const notify = useCallback((severity: Severity, message: string) => setToast({ id: Date.now(), severity, message }), []);
  const notifyError = useCallback(
    (err: unknown, context: string) => {
      console.error(context, err);
      notify('error', `${context}: ${describeError(err)}`);
    },
    [notify],
  );
  const value = useMemo(() => ({ notify, notifyError }), [notify, notifyError]);
  return (
    <NotifyContext.Provider value={value}>
      {children}
      <Snackbar
        key={toast?.id}
        open={!!toast}
        autoHideDuration={toast?.severity === 'error' ? 10_000 : 4_000}
        onClose={(_, reason) => reason !== 'clickaway' && setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} role={toast.severity === 'error' ? 'alert' : 'status'}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotifyContext.Provider>
  );
}

export function useNotify(): NotifyApi {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error('useNotify must be used inside NotifyProvider');
  return ctx;
}
